import React, { useState, useMemo } from 'react';
import './avatar.scss';
import { getConfigValue, getNameInitials } from '@zonofi/common';
import {
  AvatarProps,
  ChatSVGS,
  getAvatarBorderColor,
  getAvatarTextColor,
  getBackgroundColor,
  getEmojiFromUnicode,
} from '@zonofi/chats-common';
import { getWorkspaceId, store } from '@zonofi/seller-rtk-store';

export const Avatar: React.FC<AvatarProps> = ({
  id,
  size,
  title,
  variant = 'user',
  emojiId = '',
  imgUrl,
  className = '',
  isChannel = false,
  isSquare = false,
}) => {
  const [isImageError, setIsImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const bgColor = getBackgroundColor(id);
  const color = getAvatarTextColor(id);
  const borderColor = getAvatarBorderColor(id);
  const display = getEmojiFromUnicode(emojiId) || getNameInitials(title || id);
  const workspaceId = getWorkspaceId(store.getState());

  const mediaSrc = useMemo(() => {
    if (imgUrl) return imgUrl;
    if (workspaceId && isChannel) {
      const baseUrl = getConfigValue('S3_PUBLIC_BUCKET');
      return `${baseUrl}/workspaces/${workspaceId}/channels/${id}/profilePic.jpeg`;
    }
    return undefined;
  }, [imgUrl, workspaceId, id, isChannel]);

  const sizeClass =
    variant === 'group' ? `pn-avatar-square__${size}` : `pn-avatar__${size}`;

  const baseClass =
    variant === 'group' ? sizeClass : `pn-avatar--circle ${sizeClass}`;

  const renderFallback = () =>
    variant === 'group' ? (
      <ChatSVGS.GroupUser
        className={`pn-avatar-group-icon__${size}`}
        color={color}
      />
    ) : (
      <span className={`avatar-text comms-avatar-font-size-${size}`}>
        {display}
      </span>
    );

  const renderImage = () =>
    mediaSrc && !isImageError ? (
      <>
        {!isImageLoaded && renderFallback()}
        <img
          src={mediaSrc}
          alt="User avatar"
          className={`${variant !== 'group' ? 'avatar-image' : ''} ${baseClass}`}
          style={{ display: isImageLoaded ? 'block' : 'none' }}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => {
            setIsImageError(true);
            setIsImageLoaded(false);
          }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </>
    ) : (
      renderFallback()
    );

  return (
    <div className="pn-avatar-wrapper">
      {variant === 'group' && (
        <img
          src={ChatSVGS.GroupCards}
          className={`pn-avatar-group-card__${size}`}
          style={{ marginRight: '-1px' }}
        />
      )}
      <div
        className={`pn-avatar ${baseClass} ${variant}-avatar ${className} primary-font-regular`}
        style={{
          ...(['user', 'locked', 'group'].includes(variant)
            ? { backgroundColor: bgColor }
            : {}),
          ...(isSquare ? { borderRadius: '4px' } : {}),
        }}
      >
        {variant === 'whatsapp' ? (
          <img
            src={ChatSVGS.WhatsappIcon}
            alt="whatsapp-user"
            className={baseClass}
          />
        ) : variant === 'bot' ? (
          <img src={ChatSVGS.ZoeyIcon} alt="bot-user" className={baseClass} />
        ) : variant === 'locked' ? (
          <img src={ChatSVGS.Lock} alt="locked-user" className={baseClass} />
        ) : (
          renderImage()
        )}
      </div>
    </div>
  );
};
